/**
 * Typed API fetch layer — the single gateway between the app and the backend.
 *
 * All requests go through `authFetch` (auto-refreshes the Keycloak JWT),
 * then through here for JSON parsing, `.data` unwrapping, and error normalisation.
 *
 * Usage in query hooks:
 *   queryFn: () => apiFetch<Connector[]>(`${API_BASE}/api/v1/connectors`)
 *
 * Usage for mutations:
 *   mutationFn: (body) => apiFetch(`${API_BASE}/api/v1/alerts`, post(body))
 */

import { apiErrorMessage } from "./api-error-message";
import { authFetch } from "./fetch";

// ─── Error type ───────────────────────────────────────────────────────────────

export class ApiError extends Error {
  public readonly errorCategory?: string;
  public readonly errorSummary?: string;
  public readonly fixSuggestion?: string;
  public readonly fixAction?: Record<string, unknown>;
  public readonly executionId?: string;
  /** The parsed error body, when there was one. Kept because callers that
   *  handle a status themselves (a 404 that means "not generated yet")
   *  otherwise lose every field the endpoint sent to explain it. */
  public readonly body?: Record<string, unknown>;

  constructor(
    public readonly status: number,
    message: string,
    extra?: { errorCategory?: string; errorSummary?: string; fixSuggestion?: string; fixAction?: Record<string, unknown>; executionId?: string; body?: Record<string, unknown> },
  ) {
    super(message);
    this.name = "ApiError";
    this.errorCategory = extra?.errorCategory;
    this.errorSummary = extra?.errorSummary;
    this.fixSuggestion = extra?.fixSuggestion;
    this.fixAction = extra?.fixAction;
    this.executionId = extra?.executionId;
    this.body = extra?.body;
  }
}

// ─── Retry helper for transient network errors ──────────────────────────────
// During blue-green deployments, the GKE NEG takes 5-30s to deregister old pods.
// Requests hitting a deregistered pod get TCP RST → "Failed to fetch" in the browser.
// Retrying safe methods (GET/HEAD) covers this window transparently.

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError && (err.message === "Failed to fetch" || err.message === "NetworkError when attempting to fetch resource.")) return true;
  if (err instanceof DOMException && err.name === "AbortError") return false;
  return false;
}

function isSafeMethod(init: RequestInit | undefined): boolean {
  const method = (init?.method ?? "GET").toUpperCase();
  return method === "GET" || method === "HEAD";
}

async function fetchWithRetry(url: string, init: RequestInit | undefined, attempt = 0): Promise<Response> {
  try {
    return await authFetch(url, init);
  } catch (err) {
    if (isNetworkError(err) && isSafeMethod(init) && attempt < MAX_RETRIES) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
      return fetchWithRetry(url, init, attempt + 1);
    }
    throw err;
  }
}

// ─── Error normalisation ──────────────────────────────────────────────────────

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Build the ApiError for a non-2xx response.
 *
 * The message comes from `apiErrorMessage`, which digs the field-level reason
 * out of a validation envelope instead of echoing the generic wrapper. An
 * unparseable body (an HTML error page from a proxy, an empty 502) leaves it
 * with only the status, which is what it reports.
 */
async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // response has no JSON body — the status is all we have
  }
  const fields = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  return new ApiError(res.status, apiErrorMessage(body, res.status), {
    errorCategory: optionalString(fields.errorCategory),
    errorSummary: optionalString(fields.errorSummary),
    fixSuggestion: optionalString(fields.fixSuggestion),
    fixAction: fields.fixAction && typeof fields.fixAction === "object"
      ? (fields.fixAction as Record<string, unknown>)
      : undefined,
    executionId: optionalString(fields.executionId),
    // The inline version this helper replaced passed the parsed body, and
    // use-workflows.ts reads err.body?.lastGeneration off it -- extracting
    // without this silently drops that path.
    body: body && typeof body === "object" ? (body as Record<string, unknown>) : undefined,
  });
}

// ─── Core fetcher ─────────────────────────────────────────────────────────────

/**
 * Authenticated fetch with automatic JSON parsing and `.data` unwrapping.
 * Retries up to 2 times on network errors (GET/HEAD only) to handle brief
 * unavailability during blue-green deployments.
 * Throws `ApiError` on non-2xx responses — React Query catches these automatically.
 */
export async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetchWithRetry(url, init);

  if (!res.ok) throw await toApiError(res);

  const json = (await res.json()) as { data?: T } | T;
  return ((json as { data?: T }).data ?? json) as T;
}

/**
 * Like `apiFetch` but returns the full JSON body without `.data` unwrapping.
 * Use for paginated endpoints that return `{ data, nextCursor, total }`.
 */
export async function apiFetchRaw<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetchWithRetry(url, init);

  if (!res.ok) throw await toApiError(res);

  return (await res.json()) as T;
}

// ─── RequestInit helpers ──────────────────────────────────────────────────────

/** Builds a POST / PUT / PATCH RequestInit with JSON body. */
export function post(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export function put(body: unknown): RequestInit {
  return {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export const del: RequestInit = { method: "DELETE" };
